// batch-46 contract (FO-388) — Types, DTO's en Zod-schemas voor de contract-
// verlengingswizard met aanzegtermijn-bewaking.
//
// Kern: elk aflopend tijdelijk contract eindigt in een expliciet, geauditeerd
// besluit (verlengen / niet verlengen / omzetten naar onbepaalde tijd) PLUS een
// tijdige schriftelijke aanzegging (art. 7:668 BW). De ketenregeling-parameters
// staan hier centraal (later instelbaar); de PURE teller-functie leeft in
// `@streep/utils` (`berekenKetenregeling`). Geldbedragen (maandloon) horen NOOIT
// in een DTO/audit - ze blijven op EmployeeContractMutation.

import { z } from 'zod'

// ── Besluit-enum (mirror Prisma ContractVerlengingBesluitType) ───────────────

export const CONTRACT_VERLENGING_BESLUIT_TYPES = [
  'VERLENGEN',
  'NIET_VERLENGEN',
  'OMZETTEN_ONBEPAALDE_TIJD',
] as const
export type ContractVerlengingBesluitType = (typeof CONTRACT_VERLENGING_BESLUIT_TYPES)[number]

export const CONTRACT_VERLENGING_BESLUIT_LABELS: Record<ContractVerlengingBesluitType, string> = {
  VERLENGEN:                'Verlengen (nieuw tijdelijk contract)',
  NIET_VERLENGEN:           'Niet verlengen',
  OMZETTEN_ONBEPAALDE_TIJD: 'Omzetten naar onbepaalde tijd',
}

export const CONTRACT_VERLENGING_TRAJECT_STATUSSEN = ['ACTIEF', 'VERVALLEN'] as const
export type ContractVerlengingTrajectStatus = (typeof CONTRACT_VERLENGING_TRAJECT_STATUSSEN)[number]

// ── Ketenregeling-parameters (art. 7:668a BW hoofdregel) ─────────────────────
//
// Wettelijke hoofdregel: 3 opeenvolgende bepaalde-tijd-contracten OF > 36 maanden
// ketenduur → het volgende contract is van rechtswege voor onbepaalde tijd. Een
// onderbreking van MEER dan 6 maanden reset de keten. Als constanten hier zodat
// ze centraal staan en later instelbaar worden (A1: HR verifieert de CAO Metaal
// en Techniek-tekst vóór livegang - die wijkt voor deze doelgroep niet af).

export interface KetenregelingParams {
  /** Max. aantal opeenvolgende bepaalde-tijd-contracten vóór onbepaalde tijd. */
  maxContracten: number
  /** Max. ketenduur in maanden vóór onbepaalde tijd. */
  maxKetenMaanden: number
  /** Onderbreking (in maanden) die de keten reset (> deze waarde). */
  onderbrekingResetMaanden: number
}

export const KETENREGELING_PARAMS: KetenregelingParams = {
  maxContracten: 3,
  maxKetenMaanden: 36,
  onderbrekingResetMaanden: 6,
}

/** Minimale looptijd (maanden) waarboven de aanzegplicht geldt (art. 7:668 BW). */
export const AANZEGPLICHT_MIN_MAANDEN = 6

/** Dagen vóór einddatum waarop STREEP hard escaleert zonder besluit/aanzegging. */
export const AANZEG_ESCALATIE_DAGEN = 30

// ── DTO's ────────────────────────────────────────────────────────────────────

/** Serialiseerbare ketenteller-uitkomst (Date's als ISO-datum-strings). */
export interface KetenregelingDTO {
  /** Aantal bepaalde-tijd-contracten in de huidige (niet-onderbroken) keten. */
  aantalContracten: number
  /** Totale ketenduur in hele maanden. */
  ketenduurMaanden: number
  /** Startdata (ISO) waar een onderbreking > 6 mnd de keten resette. */
  resetMomenten: string[]
  /** Het volgende contract is wettelijk voor onbepaalde tijd (3e of > 36 mnd). */
  volgendVerplichtOnbepaald: boolean
  /**
   * De historie is mogelijk onvolledig (geen mutaties vóór de v3-migratie): toon
   * "mogelijk onvolledig - controleer het papieren dossier" i.p.v. een vals-zeker
   * getal (§5.6, nooit gokken).
   */
  mogelijkOnvolledig: boolean
}

/** Eén regel van de contractmutatie-tijdlijn (stap 1 context, geen bedragen). */
export interface VerlengTijdlijnRegelDTO {
  id: string
  effectiveFrom: string // ISO-date
  contractType: string
  contractTypeLabel: string
  eindDatum: string | null
  urenPerWeek: number | null
  reden: string
}

/** Bestaand besluit (bij een tweede wizard-start ter correctie, §5.3). */
export interface ContractVerlengingsBesluitDTO {
  id: string
  employmentContractId: string
  status: ContractVerlengingTrajectStatus
  besluit: ContractVerlengingBesluitType
  besluitLabel: string
  besluitOp: string
  motivatie: string | null
  ketenOverride: boolean
  ketenOverrideMotivatie: string | null
  aanzegbriefDocumentId: string | null
  /** Hoogste versienr van de aanzegbrief (voor de same-origin download-proxy). */
  aanzegbriefVersieNr: number | null
  /** De brief is buiten de app gemaakt en geüpload (niet uit het sjabloon gegenereerd). */
  aanzegbriefGeupload: boolean
  aangezegdOp: string | null
}

/**
 * Volledige context voor stap 1 van de verleng-wizard: het aflopende contract,
 * de mutatie-tijdlijn, de ketenteller, het aanzegplicht-predicaat en een
 * eventueel bestaand besluit. Bevat GEEN salarisbedragen (§3.6).
 */
export interface VerlengWizardContextDTO {
  contractId: string
  employeeId: string
  medewerkerNaam: string
  contractType: string | null
  contractTypeLabel: string | null
  startDatum: string
  eindDatum: string | null
  /** Dagen tot einddatum (negatief = verlopen); null bij onbepaalde tijd. */
  dagenTotEinde: number | null
  keten: KetenregelingDTO
  /** Bepaalde tijd én looptijd ≥ 6 maanden → aanzegplichtig (§5.2). */
  aanzegplichtig: boolean
  tijdlijn: VerlengTijdlijnRegelDTO[]
  besluit: ContractVerlengingsBesluitDTO | null
  /**
   * FO-459 (batch-62) - het sjabloon dat de aanzegbrief krijgt (dezelfde selectie
   * als de generatie); de briefpapier-kiezer leest er de template-standaard en de
   * eigen-briefpapier-vlag mee. Null = geen aanzegplicht of nog geen sjabloon.
   */
  aanzegbriefTemplateId: string | null
  /**
   * 2026-10 - het sjabloon heeft een Word-bestand, dus de aanzegbrief kan echt
   * gegenereerd worden. False = de wizard toont vooraf waarom de stap vastzit en
   * waar het op te lossen is (i.p.v. pas een fout na klikken).
   */
  aanzegbriefTemplateKlaar: boolean
}

// ── Zod-schemas (request bodies) ─────────────────────────────────────────────

/**
 * Gedeeld besluit-schema: de API gebruikt het (`safeParse`) én het form valideert
 * er client-side mee (één bron, geen onterechte Validatiefout). Motivatie is
 * verplicht bij NIET_VERLENGEN (reden) en bij een keten-override (ADMIN).
 */
export const contractVerlengingBesluitSchema = z
  .object({
    besluit: z.enum(CONTRACT_VERLENGING_BESLUIT_TYPES),
    motivatie: z.string().trim().max(2000).nullable().optional(),
    /** ADMIN-override op de ketenwaarschuwing: VERLENGEN bepaalde tijd bij volle keten. */
    ketenOverride: z.boolean().optional(),
    ketenOverrideMotivatie: z.string().trim().max(2000).nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.besluit === 'NIET_VERLENGEN' && !(val.motivatie && val.motivatie.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['motivatie'],
        message: 'Geef een reden op waarom niet wordt verlengd.',
      })
    }
    if (val.ketenOverride && !(val.ketenOverrideMotivatie && val.ketenOverrideMotivatie.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ketenOverrideMotivatie'],
        message: 'Een keten-override vereist een motivatie.',
      })
    }
  })
export type ContractVerlengingBesluitInput = z.infer<typeof contractVerlengingBesluitSchema>

/** Labels voor `summarizeValidationIssues` (inline veld-fout-fallback). */
export const CONTRACT_VERLENGING_FIELD_LABELS: Record<string, string> = {
  besluit:                'Besluit',
  motivatie:              'Motivatie',
  ketenOverride:          'Keten-override',
  ketenOverrideMotivatie: 'Override-motivatie',
}
